import React, { useEffect, useState } from 'react';
import { FiX } from 'react-icons/fi';

export interface DrawerProps {
  isOpen: boolean;
  onClose: () => void;
  size?: 'md' | 'lg';
  icon?: React.ReactNode;
  title?: string;
  subtitle?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  disabled?: boolean;
}

const SIZE_MAP = {
  md: 'w-[624px]',
  lg: 'w-[960px]',
} as const;

export const Drawer: React.FC<DrawerProps> = ({
  isOpen,
  onClose,
  size = 'md',
  icon,
  title = '',
  subtitle,
  children,
  footer,
  disabled = false,
}) => {
  const [shouldRender, setShouldRender] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      const raf = requestAnimationFrame(() => setIsAnimating(true));
      return () => cancelAnimationFrame(raf);
    } else {
      setIsAnimating(false);
      const timer = setTimeout(() => setShouldRender(false), 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const handleClose = () => {
    if (disabled) return;
    onClose();
  };

  if (!shouldRender) return null;

  return (
    <div
      className="fixed inset-0 z-50"
      style={{
        backgroundColor: isAnimating ? 'rgba(0, 0, 0, 0.5)' : 'rgba(0, 0, 0, 0)',
        transition: 'background-color 300ms ease',
      }}
      onClick={handleClose}
    >
      <div
        className={`fixed right-0 top-0 h-full ${SIZE_MAP[size]} max-w-full flex flex-col`}
        style={{
          backgroundColor: '#ffffff',
          boxShadow: '-4px 0 24px rgba(0, 0, 0, 0.12)',
          transform: isAnimating ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 300ms ease',
          '--theme-text': '#1e293b',
          '--theme-muted-text': '#6b7280',
          '--theme-input-background': '#ffffff',
          '--theme-input-text': '#1e293b',
          '--theme-input-border': 'rgba(0, 0, 0, 0.10)',
          '--theme-input-focus': '#8b5cf6',
          '--theme-error-color': '#ef4444',
          '--theme-card-background': '#ffffff',
          '--theme-card-text': '#334155',
          '--theme-card-border': 'rgba(0, 0, 0, 0.06)',
          '--theme-card-muted': '#94a3b8',
          '--theme-button-primary': '#8b5cf6',
          '--theme-button-primary-text': '#ffffff',
          '--theme-button-primary-hover': '#7c3aed',
          '--theme-button-secondary': '#f1f5f9',
          '--theme-button-secondary-text': '#475569',
          '--theme-button-secondary-hover': '#e2e8f0',
          '--theme-button-ghost': 'transparent',
          '--theme-button-ghost-hover': '#f1f5f9',
          '--theme-primary': '#8b5cf6',
          '--theme-border': 'rgba(0, 0, 0, 0.06)',
          '--theme-component-background': '#ffffff',
        } as React.CSSProperties}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-6 py-4 border-b flex-shrink-0"
          style={{ borderColor: '#e5e7eb' }}
        >
          <div className="flex items-center gap-3">
            {icon && (
              <div
                className="p-2 rounded-lg"
                style={{ backgroundColor: '#f3e8ff', color: '#8b5cf6' }}
              >
                {icon}
              </div>
            )}
            <div>
              <h2 className="text-lg font-semibold" style={{ color: '#1e293b' }}>
                {title}
              </h2>
              {subtitle && (
                <p className="text-sm" style={{ color: '#64748b' }}>
                  {subtitle}
                </p>
              )}
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-1.5 rounded-lg transition-colors"
            style={{ color: '#64748b' }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#f1f5f9';
              e.currentTarget.style.color = '#1e293b';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = '#64748b';
            }}
            disabled={disabled}
          >
            <FiX className="w-5 h-5" />
          </button>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {children}
        </div>

        {/* Footer */}
        {footer && (
          <div
            className="flex items-center justify-end gap-3 px-6 py-4 border-t flex-shrink-0"
            style={{ backgroundColor: '#f9fafb', borderColor: '#e5e7eb' }}
          >
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};
